// Rate Constants matching Backend configuration
export const TOKEN_RATES = {
  TRANSLATION_CHARS_PER_TOKEN: Number(process.env.REACT_APP_TRANSLATION_CHARS_PER_TOKEN) || 4, // 4 characters = 1 token
  TTS_CHARS_PER_TOKEN: Number(process.env.REACT_APP_TOKEN_RATE_TTS_CHARS) || 2,         // 2 characters = 1 token
  STT_TOKENS_PER_SECOND: Number(process.env.REACT_APP_TOKEN_RATE_STT_TOKENS_PER_SECOND) || 3, // 1 second = 3 tokens
  ST_TOKENS_PER_SECOND: Number(process.env.REACT_APP_TOKEN_RATE_ST_TOKENS_PER_SECOND) || 5,   // 1 second = 5 tokens
};
/**
 * Estimate tokens for text-based input (Text Translation & Text-to-Speech)
 * @param {string} text - Input text string
 * @param {number} charsPerToken - Characters per token rate (default 2 for TTS, 4 for translation)
 * @returns {number} Estimated tokens
 */
export const estimateTextTokens = (text, charsPerToken = TOKEN_RATES.TTS_CHARS_PER_TOKEN) => {
  if (!text || typeof text !== "string" || !text.trim()) return 0;
  const rate = Number.isFinite(charsPerToken) && charsPerToken > 0 ? charsPerToken : 4;
  return Math.max(1, Math.ceil(text.trim().length / rate));
};

/**
 * Estimate tokens for audio-based input (Speech-to-Text & Speech Translation)
 * Formula: Math.ceil(durationInSeconds * tokensPerSecond)
 * @param {number} durationInSeconds - Audio length in seconds
 * @param {number} tokensPerSecond - Tokens per 1 second of audio (default 3 tokens/sec for STT, 5 for ST)
 * @returns {number} Estimated tokens
 */
export const estimateAudioTokens = (durationInSeconds, tokensPerSecond = TOKEN_RATES.STT_TOKENS_PER_SECOND) => {
  if (!Number.isFinite(durationInSeconds) || isNaN(durationInSeconds) || durationInSeconds <= 0) return 0;
  const rate = Number.isFinite(tokensPerSecond) && tokensPerSecond > 0 ? tokensPerSecond : 3;
  const billedSeconds = Math.ceil(durationInSeconds);
  return Math.max(1, billedSeconds * rate);
};

/**
 * Reads audio file metadata and decodes buffer to obtain exact duration in seconds
 * Handles WAV, MP3, WebM, OGG, FLAC and prevents Infinity / NaN duration bugs.
 * @param {File|Blob} file - Audio file object
 * @returns {Promise<number>} Duration in seconds (always a safe finite number >= 0)
 */
export const getAudioFileDuration = async (file) => {
  if (!file) return 0;

  // Method 1: Web Audio API decodeAudioData (Most accurate for WAV headers and WebM files)
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      const audioCtx = new AudioContextClass();
      try {
        const arrayBuffer = await file.arrayBuffer();
        const decodedBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
        const dur = decodedBuffer?.duration;
        if (Number.isFinite(dur) && dur > 0) {
          return dur;
        }
      } finally {
        if (audioCtx.state !== 'closed') {
          audioCtx.close().catch(() => { });
        }
      }
    }
  } catch (err) {
    console.warn("Web Audio decodeAudioData fallback triggered:", err);
  }

  // Method 2: HTML Audio element metadata fallback
  return new Promise((resolve) => {
    try {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      audio.preload = "metadata";
      audio.src = url;

      let resolved = false;
      const cleanupAndResolve = (val) => {
        if (resolved) return;
        resolved = true;
        try {
          URL.revokeObjectURL(url);
          audio.removeAttribute("src");
        } catch (_) { }
        const safeVal = Number.isFinite(val) && !isNaN(val) && val > 0 ? val : 0;
        resolve(safeVal);
      };

      audio.onloadedmetadata = () => {
        const dur = audio.duration;
        if (!Number.isFinite(dur) || isNaN(dur) || dur === Infinity) {
          audio.currentTime = 1e101;
          audio.ontimeupdate = () => {
            audio.ontimeupdate = null;
            let actualDuration = audio.duration;
            if (!Number.isFinite(actualDuration) || isNaN(actualDuration) || actualDuration === Infinity) {
              actualDuration = audio.currentTime;
            }
            cleanupAndResolve(actualDuration);
          };
          setTimeout(() => cleanupAndResolve(0), 1000);
        } else {
          cleanupAndResolve(dur);
        }
      };

      audio.onerror = () => cleanupAndResolve(0);
      setTimeout(() => cleanupAndResolve(0), 3000);
    } catch (_) {
      resolve(0);
    }
  });
};
